import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Globe, X } from 'lucide-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.85rem;
  background: ${({ theme }) => theme?.colors?.background || '#fff'};
  border: 1px solid ${({ theme }) => theme?.colors?.border || '#e5e7eb'};
  border-radius: ${({ theme }) => theme?.borderRadius?.lg || '0.5rem'};
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.foreground || '#111827'};
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: ${({ theme }) => theme?.colors?.muted || '#f3f4f6'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }
`;

const FlagIcon = styled.img`
  width: 1.25rem;
  height: 0.9rem;
  object-fit: cover;
  border-radius: 2px;
  flex-shrink: 0;
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: hsl(0 0% 0% / 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const Panel = styled.div`
  width: min(560px, 100%);
  max-height: min(80vh, 640px);
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme?.colors?.card || theme?.colors?.background || '#fff'};
  border: 1px solid ${({ theme }) => theme?.colors?.border || '#e5e7eb'};
  border-radius: 1rem;
  box-shadow: 0 20px 50px hsl(220 20% 10% / 0.25);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.15rem;
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.border || '#e5e7eb'};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${({ theme }) => theme?.colors?.foreground || '#111827'};
`;

const CloseBtn = styled.button`
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 9999px;
  background: transparent;
  color: ${({ theme }) => theme?.colors?.mutedForeground || '#6b7280'};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme?.colors?.muted || '#f3f4f6'};
    color: ${({ theme }) => theme?.colors?.foreground || '#111827'};
  }

  svg {
    width: 1.1rem;
    height: 1.1rem;
  }
`;

const Search = styled.input`
  margin: 0.85rem 1.15rem 0;
  padding: 0.65rem 0.85rem;
  border: 1px solid ${({ theme }) => theme?.colors?.border || '#e5e7eb'};
  border-radius: 0.65rem;
  background: ${({ theme }) => theme?.colors?.background || '#fff'};
  color: ${({ theme }) => theme?.colors?.foreground || '#111827'};
  font-size: 0.875rem;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme?.colors?.primary || '#3b82f6'};
  }
`;

const List = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.85rem 1.15rem 1.15rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.4rem;

  @media (min-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Option = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 0.85rem;
  text-align: left;
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme?.colors?.primary || '#3b82f6' : theme?.colors?.border || '#e5e7eb'};
  border-radius: 0.75rem;
  background: ${({ $active, theme }) =>
    $active ? `${theme?.colors?.primary || '#3b82f6'}14` : 'transparent'};
  color: ${({ theme }) => theme?.colors?.foreground || '#111827'};
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: ${({ theme }) => theme?.colors?.muted || '#f3f4f6'};
  }
`;

const OptionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
`;

const OptionName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25;
`;

const OptionLang = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme?.colors?.mutedForeground || '#6b7280'};
`;

const LANGUAGE_NAMES = {
  en: 'English',
  fr: 'French',
  es: 'Spanish',
  de: 'German',
  it: 'Italian',
  pt: 'Portuguese',
  sv: 'Swedish',
  fi: 'Finnish',
  da: 'Danish',
  no: 'Norwegian',
  nl: 'Dutch',
};

const REGION_TO_FLAG = {
  US: 'us',
  GB: 'gb',
  CA: 'ca',
  AU: 'au',
  NZ: 'nz',
  IE: 'ie',
  JM: 'jm',
  MX: 'mx',
  BR: 'br',
  AR: 'ar',
  CO: 'co',
  CL: 'cl',
  PA: 'pa',
  DO: 'do',
  IN: 'in',
  MY: 'my',
  SG: 'sg',
  PH: 'ph',
  VN: 'vn',
  KH: 'kh',
  MM: 'mm',
  HK: 'hk',
  FR: 'fr',
  LU: 'lu',
  ES: 'es',
  DE: 'de',
  CH: 'ch',
  AT: 'at',
  IT: 'it',
  PT: 'pt',
  SE: 'se',
  FI: 'fi',
  DK: 'dk',
  NO: 'no',
  NL: 'nl',
  BE: 'be',
};

function flagForRegion(code) {
  return REGION_TO_FLAG[code] || String(code || 'us').toLowerCase();
}

const LanguageSwitcher = () => {
  const { t } = useTranslation();
  const { region, availableRegions, switchLanguage, loading } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const regionOptions = useMemo(() => {
    const options = (availableRegions || [])
      .filter((item) => item?.defaultLanguage && item.isActive !== false)
      .map((item) => {
        const lang = String(item.defaultLanguage).toLowerCase();
        return {
          code: lang,
          languageName: LANGUAGE_NAMES[lang] || lang.toUpperCase(),
          regionCode: item.code,
          regionName: item.name,
          flag: flagForRegion(item.code),
        };
      });

    options.sort((a, b) => {
      const byRegion = a.regionName.localeCompare(b.regionName);
      if (byRegion !== 0) return byRegion;
      return a.languageName.localeCompare(b.languageName);
    });
    return options;
  }, [availableRegions]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return regionOptions;
    return regionOptions.filter(
      (option) =>
        option.regionName.toLowerCase().includes(q) ||
        option.languageName.toLowerCase().includes(q) ||
        option.regionCode.toLowerCase().includes(q) ||
        option.code.toLowerCase().includes(q)
    );
  }, [regionOptions, query]);

  const currentRegion =
    region || availableRegions.find((r) => r.code === 'US') || availableRegions[0];

  const handleSwitch = (lang, regionCode) => {
    switchLanguage(lang, regionCode);
    setOpen(false);
  };

  return (
    <>
      <Trigger
        type="button"
        disabled={loading}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('nav.language', 'Language')}
      >
        <Globe aria-hidden />
        <span>{t('nav.language', 'Language')}</span>
      </Trigger>

      {open &&
        createPortal(
          <Backdrop
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
          >
            <Panel
              role="dialog"
              aria-modal="true"
              aria-labelledby="language-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <Header>
                <Title id="language-modal-title">
                  {t('nav.chooseLanguage', 'Choose your language / region')}
                </Title>
                <CloseBtn
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label={t('common.close', 'Close')}
                >
                  <X />
                </CloseBtn>
              </Header>

              <Search
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('nav.searchLanguage', 'Search country or language…')}
                autoFocus
              />

              <List>
                {filteredOptions.map((option) => {
                  const isActive = option.regionCode === currentRegion?.code;
                  return (
                    <Option
                      key={`${option.regionCode}-${option.code}`}
                      type="button"
                      $active={isActive}
                      onClick={() => handleSwitch(option.code, option.regionCode)}
                    >
                      <FlagIcon
                        src={`https://flagcdn.com/${option.flag}.svg`}
                        alt=""
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <OptionText>
                        <OptionName>{option.regionName}</OptionName>
                        <OptionLang>{option.languageName}</OptionLang>
                      </OptionText>
                    </Option>
                  );
                })}
              </List>
            </Panel>
          </Backdrop>,
          document.body
        )}
    </>
  );
};

export default LanguageSwitcher;
